/**
 * RTM listener for outbound:sent events.
 * Shows an info toast when WhatsApp API accepts the outbound message.
 * Only fires for the agent who initiated the message.
 */
import { useCallback, useContext, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { SocketContext } from '@/contexts/rtm';
import { selectUserId } from '@/redux/user/selectors';
import { OutboundSentPayload, ServerToClientEvents, SocketEvent } from '@/shared/types/rtm';

export const useRtmOutboundSent = () => {
  const { socket } = useContext(SocketContext);
  const userId = useSelector(selectUserId);
  const { t } = useTranslation();

  const handleOutboundSent: ServerToClientEvents[SocketEvent.outbound_sent] = useCallback(
    ({ data }: { data: OutboundSentPayload }) => {
      if (data.initiator_user_id !== userId) {
        return;
      }

      toast.info(t('conversation.outbound_sent'));
    },
    [t, userId]
  );

  useEffect(() => {
    socket?.on(SocketEvent.outbound_sent, handleOutboundSent);
    return () => {
      socket?.off(SocketEvent.outbound_sent, handleOutboundSent);
    };
  }, [handleOutboundSent, socket]);
};
